import React, { useCallback } from 'react';
import { msalInstance } from '../../msalConfig';

interface AdminContextType {
  authToken: string;
  setAuthToken: React.Dispatch<React.SetStateAction<string>>;
  authorizeAdmin: () => Promise<boolean>;
  loading: boolean;
  logout: () => void;
  tokenSet: boolean;
}

export const AdminContext = React.createContext<AdminContextType>(
  {} as AdminContextType
);

export function AdminProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [authToken, setAuthToken] = React.useState<string>('');
  const [loading, setLoading] = React.useState<boolean>(true);
  const [tokenSet, setTokenSet] = React.useState<boolean>(false);
  const STORAGE_KEY = 'admin_token';

  React.useEffect(() => {
    const foundToken = localStorage.getItem(STORAGE_KEY) as string;
    if (foundToken) {
      setAuthToken(foundToken);
    }
    setTokenSet(true);
  }, []);

  // Handle redirect callback when MSAL redirects the user
  const handleRedirectCallback = useCallback(() => {
    setLoading(true);

    msalInstance
      .handleRedirectPromise()
      .then((loginResponse) => {
        if (loginResponse && loginResponse.account) {
          // Try to get access token, fall back to ID token
          const token = loginResponse.accessToken || loginResponse.idToken;

          if (token) {
            setAuthToken(token);
            localStorage.setItem(STORAGE_KEY, token);
            // Don't use window.location.href - let React Router handle navigation
          } else {
            console.error('No token received in login response');
          }
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error('Error during redirect callback:', error);
        setLoading(false);
      });
  }, []);

  // Run handleRedirectCallback when the component mounts
  React.useEffect(() => {
    handleRedirectCallback();
  }, [handleRedirectCallback]);

  // Authorize the admin via MSAL login using the redirect flow
  const authorizeAdmin = useCallback(async () => {
    setLoading(true);
    try {
      const clientId =
        process.env.REACT_APP_MSAL_CLIENT_ID ||
        'd18faf6a-6313-41e3-ba63-3ed2e4d693a8';
      // Using loginRedirect to redirect user to login page
      await msalInstance.loginRedirect({
        scopes: [`${clientId}/.default`],
        extraQueryParameters: {
          response_mode: 'query', // Force query parameters instead of fragment
        },
      });
      return true;
    } catch (error) {
      console.error('Authorization failed:', error);
      setAuthToken('');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    msalInstance.logoutRedirect({
      postLogoutRedirectUri: `${window.location.origin}/admin/login`,
    });
    setAuthToken('');
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const memoizedValue = React.useMemo(
    () => ({
      authToken,
      setAuthToken,
      authorizeAdmin,
      loading,
      logout,
      tokenSet,
    }),
    [authToken, authorizeAdmin, loading, logout, tokenSet]
  );

  return (
    <AdminContext.Provider value={memoizedValue}>
      {children}
    </AdminContext.Provider>
  );
}
